import PropTypes from "prop-types"
import React, { useEffect, useState, useMemo } from "react"
import {
  Col,
  Row,
  Container,
  Button,
  Modal,
  ModalBody,
} from "reactstrap"

import Breadcrumbs from "components/Common/Breadcrumb2"
import { withTranslation } from "react-i18next"
import "components/Common/ts-css.css"

import { get, post, put } from "helpers/api_helper"

import * as Yup from "yup"
import { useFormik } from "formik"

import Spinners from "components/Common/Spinner"
import { useNavigate, useParams } from "react-router-dom"
import moment from "moment"
import { toast } from "react-toastify"

// Step Components
import Step1 from "./components/Step1"
import Step2 from "./components/Step2"

const CancelModal = ({ show, onConfirm, onCancel }) => (
  <Modal size="md" isOpen={show} toggle={onCancel} centered>
    <ModalBody className="px-4 py-4">
      <h5 className="mb-4" style={{ fontWeight: "600" }}>Are you sure to cancel?</h5>
      <div className="d-flex gap-2 justify-content-end mt-4">
        <button
          type="button"
          className="btn btn-outline-secondary"
          style={{
            minWidth: "100px",
            backgroundColor: "#fff",
            border: "1px solid #ced4da",
            color: "#495057",
            borderRadius: "8px",
          }}
          onClick={onConfirm}
        >
          Yes
        </button>
        <button
          type="button"
          className="btn btn-success"
          style={{
            minWidth: "100px",
            backgroundColor: "#34c38f",
            border: "1px solid #34c38f",
            borderRadius: "8px",
          }}
          onClick={onCancel}
        >
          No
        </button>
      </div>
    </ModalBody>
  </Modal>
)

const SurveyLineCampaignTemplate = ({ pageView = "create" }) => {
  const navigate = useNavigate()
  const { id } = useParams()

  const [isLoading, setIsLoading] = useState(true)
  const [currentStep, setCurrentStep] = useState(1)
  const [showCancelModal, setShowCancelModal] = useState(false)
  
  // States
  const [categoryOptions, setCategoryOptions] = useState([])
  const [allSurveyOptions, setAllSurveyOptions] = useState([])
  const [lineOaOptions, setLineOaOptions] = useState([])


  const validationSchema = Yup.object({
    lineOaId: Yup.string().required("Please Select LINE OA"),
    categoryId: Yup.string().required("Please Select Category"),
    surveyId: Yup.string().required("Please Select Survey"),
    recipientsLabel: Yup.string().max(100, "Max 100 characters"),
    specificUserLineUids: Yup.array(),
    startDate: Yup.date()
      .required("Please Select Start Date")
      .min(moment().startOf('day'), "Start date must be today or later"),
    endDate: Yup.date()
      .required("Please Select End Date")
      .min(Yup.ref("startDate"), "End date must be after start date"),
  })

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      id: ["create"].includes(pageView) ? undefined : "",
      lineOaId: "",
      categoryId: "",
      surveyId: "",
      surveyUrl: "",
      surveyMobileUrl: "",
      recipients: "all",
      recipientsLabel: "",
      audienceGender: [],
      audienceAge: [],
      audienceOs: [],
      audienceFriendshipPeriod: [],
      audienceCity: [],
      broadcastTimeType: "now",
      scheduleDate: "",
      scheduleTime: "",
      contentBlocks: [
        { id: "block-1", type: "text", textContent: "", imageUrl: "", imageTitle: "", imageAlt: "" },
      ],
      startDate: "",
      endDate: "",
      campaignName: "",
      specificUserLineUids: [],
      specificUserFileName: "",
      status: "draft",
    },
    validationSchema: validationSchema,
    onSubmit: async values => {
      const normalizedContentBlocks = (values.contentBlocks || []).map(block => ({
        ...block,
        type: block?.type === "pictures" ? "image" : (block?.type || "text"),
      }))

      const payload = {
        line_oa_id: values.lineOaId ? Number(values.lineOaId) : null,
        campaign_name: values.campaignName,
        category_id: values.categoryId ? Number(values.categoryId) : null,
        survey_id: values.surveyId ? Number(values.surveyId) : null,
        survey_url: values.surveyUrl || "",
        recipients: values.recipients || "targeting",
        recipients_label: values.recipients === "all" ? "" : (values.recipientsLabel || ""),
        audience_gender: values.recipients === "all" ? [] : (values.audienceGender || []),
        audience_age: values.recipients === "all" ? [] : (values.audienceAge || []),
        audience_os: values.recipients === "all" ? [] : (values.audienceOs || []),
        audience_friendship_period: values.recipients === "all" ? [] : (values.audienceFriendshipPeriod || []),
        audience_city: values.recipients === "all" ? [] : (values.audienceCity || []),
        broadcast_time_type: values.broadcastTimeType || "now",
        schedule_date: values.scheduleDate ? moment.utc(values.scheduleDate).format("YYYY-MM-DD") : null,
        schedule_time: values.scheduleTime || null,
        content_blocks: normalizedContentBlocks,
        specific_user_line_uids: values.specificUserLineUids || [],
        start_date: values.startDate ? moment.utc(values.startDate).toISOString() : null,
        end_date: values.endDate ? moment.utc(values.endDate).toISOString() : null,
        status: values.status,
      }

      try {
        if (["create"].includes(pageView)) {
          await post(
            process.env.REACT_APP_BACKEND_URL + "/v1/admin/line-campaign",
            payload
          )
          toast.success("Campaign created successfully")
        } else {
          await put(
            process.env.REACT_APP_BACKEND_URL +
              "/v1/admin/line-campaign/" +
              id,
            payload
          )
          toast.success("Campaign updated successfully")
        }
        
        navigate("/survey-line-campaign")
      } catch (error) {
        console.log("error", error)
        toast.error("Error saving campaign")
      }
    },
  })

  // Step 1 Filtered Survey Options
  const filteredSurveyOptions = useMemo(() => {
    const selectedCategoryId = validation.values.categoryId
    if (!selectedCategoryId) return []

    const selectedCategory = categoryOptions.find(cat => String(cat.id) === String(selectedCategoryId))
    if (!selectedCategory) return []

    return allSurveyOptions.filter(survey => survey.category_name === selectedCategory.category_name)
  }, [validation.values.categoryId, categoryOptions, allSurveyOptions])

  // Get selected survey name
  const selectedSurveyName = useMemo(() => {
    const selectedSurveyId = validation.values.surveyId
    if (!selectedSurveyId) return ""

    const selectedSurvey = allSurveyOptions.find(survey => String(survey.id) === String(selectedSurveyId))
    return selectedSurvey ? selectedSurvey.name : ""
  }, [validation.values.surveyId, allSurveyOptions])

  const preFillForm = (formData = {}) => {
    const normalizedContentBlocks = Array.isArray(formData.content_blocks)
      ? formData.content_blocks.map(block => ({
          ...block,
          type: block?.type === "pictures" ? "image" : (block?.type || "text"),
        }))
      : []

    validation.setFieldValue("id", formData.id)
    validation.setFieldValue("lineOaId", formData.line_oa_id || "")
    validation.setFieldValue("categoryId", formData.category_id || "")
    validation.setFieldValue("surveyId", formData.survey_id || "")
    validation.setFieldValue("surveyUrl", formData.survey_url || "")
    validation.setFieldValue("recipients", formData.recipients || "targeting")
    validation.setFieldValue("recipientsLabel", formData.recipients_label || "")
    validation.setFieldValue("audienceGender", Array.isArray(formData.audience_gender) ? formData.audience_gender : [])
    validation.setFieldValue("audienceAge", Array.isArray(formData.audience_age) ? formData.audience_age : [])
    validation.setFieldValue("audienceOs", Array.isArray(formData.audience_os) ? formData.audience_os : [])
    validation.setFieldValue("audienceFriendshipPeriod", Array.isArray(formData.audience_friendship_period) ? formData.audience_friendship_period : [])
    validation.setFieldValue("audienceCity", Array.isArray(formData.audience_city) ? formData.audience_city : [])
    validation.setFieldValue("broadcastTimeType", formData.broadcast_time_type || "now")
    validation.setFieldValue("scheduleDate", formData.schedule_date ? moment.utc(formData.schedule_date).format("YYYY-MM-DD") : "")
    validation.setFieldValue("scheduleTime", formData.schedule_time || "")
    validation.setFieldValue("contentBlocks", normalizedContentBlocks.length > 0 ? normalizedContentBlocks : [
      { id: "block-1", type: "image", textContent: "", imageUrl: "", imageTitle: "", imageAlt: "" },
      { id: "block-2", type: "text", textContent: "", imageUrl: "", imageTitle: "", imageAlt: "" },
    ])
    
    if (formData.start_date) {
      validation.setFieldValue("startDate", moment.utc(formData.start_date).format("YYYY-MM-DD"))
    }
    if (formData.end_date) {
      validation.setFieldValue("endDate", moment.utc(formData.end_date).format("YYYY-MM-DD"))
    }
    
    validation.setFieldValue("campaignName", formData.campaign_name || "")
    validation.setFieldValue("status", formData.status || "draft")
  }

  useEffect(() => {
    const fetchDropdownData = async () => {
      try {
        const requestList = [
          get(process.env.REACT_APP_BACKEND_URL + "/v1/admin/dropdown/category"),
          get(process.env.REACT_APP_BACKEND_URL + "/v1/admin/dropdown/questionnaire"),
          get(process.env.REACT_APP_BACKEND_URL + "/v1/admin/line-oa/dropdown"),
        ]

        if (["create"].includes(pageView)) {
          requestList.push(
            get(process.env.REACT_APP_BACKEND_URL + "/v1/admin/line-campaign/next-id")
          )
        }

        const [catResult, surveyResult, lineOaResult, nextIdResult] = await Promise.all(requestList)

        if (catResult && catResult.data) setCategoryOptions(catResult.data)
        if (surveyResult && surveyResult.data) setAllSurveyOptions(surveyResult.data)
        if (lineOaResult && lineOaResult.data) setLineOaOptions(lineOaResult.data)
        if (["create"].includes(pageView) && nextIdResult?.data?.next_id) {
          validation.setFieldValue("id", nextIdResult.data.next_id)
        }
      } catch (error) {
        console.error("Error fetching dropdown data:", error)
      }
    }
    fetchDropdownData()

    if (["update"].includes(pageView) && id) {
      const fetchData = async () => {
        try {
          const result = await get(
            process.env.REACT_APP_BACKEND_URL +
              "/v1/admin/line-campaign/" +
              id
          )
          const formData = result.data
          preFillForm(formData)
        } catch (error) {
          console.log("error", error)
        }
        setIsLoading(false)
      }
      fetchData()
    } else {
      setIsLoading(false)
    }
  }, [pageView, id])

  // Clear surveyId when categoryId changes
  const [prevCategoryId, setPrevCategoryId] = useState("")
  useEffect(() => {
    if (prevCategoryId !== validation.values.categoryId && prevCategoryId !== "") {
      validation.setFieldValue("surveyId", "")
    }
    setPrevCategoryId(validation.values.categoryId)
  }, [validation.values.categoryId])

  const buildSurveyUrls = (surveyUuidLink, lineOaId, campaignId) => {
    const base = `${process.env.REACT_APP_FRONTWEB_URL}/pdpa/chat/${surveyUuidLink}`
    const selectedOa = lineOaOptions.find(o => String(o.id) === String(lineOaId))

    const lineParams = []
    if (selectedOa && selectedOa.channel_id_login) {
      lineParams.push(`cid=${selectedOa.channel_id_login}`)
    }
    if (campaignId) {
      lineParams.push(`lcid=${campaignId}`)
    }
    const surveyUrl = lineParams.length > 0 ? `${base}?${lineParams.join("&")}` : base

    const surveyMobileUrl = campaignId ? `${base}?lcid=${campaignId}` : base

    return { surveyUrl, surveyMobileUrl }
  }

  const setSurveyUrls = (surveyUuidLink, lineOaId, campaignId) => {
    const { surveyUrl, surveyMobileUrl } = buildSurveyUrls(surveyUuidLink, lineOaId, campaignId)
    validation.setFieldValue("surveyUrl", surveyUrl)
    validation.setFieldValue("surveyMobileUrl", surveyMobileUrl)
  }

  // Auto-fill survey URLs when surveyId changes
  const [prevSurveyId, setPrevSurveyId] = useState("")
  useEffect(() => {
    if (prevSurveyId !== validation.values.surveyId && validation.values.surveyId !== "") {
      const selectedSurvey = allSurveyOptions.find(s => String(s.id) === String(validation.values.surveyId))
      if (selectedSurvey && selectedSurvey.uuid_link) {
        setSurveyUrls(selectedSurvey.uuid_link, validation.values.lineOaId, validation.values.id)
      }
    } else if (validation.values.surveyId === "") {
      validation.setFieldValue("surveyUrl", "")
      validation.setFieldValue("surveyMobileUrl", "")
    }
    setPrevSurveyId(validation.values.surveyId)
  }, [validation.values.surveyId, allSurveyOptions])

  // Auto-fill survey URLs from dropdown when in edit mode (after dropdown data is loaded)
  useEffect(() => {
    if (["update"].includes(pageView) && validation.values.surveyId && allSurveyOptions.length > 0) {
      const selectedSurvey = allSurveyOptions.find(s => String(s.id) === String(validation.values.surveyId))
      if (selectedSurvey && selectedSurvey.uuid_link) {
        setSurveyUrls(selectedSurvey.uuid_link, validation.values.lineOaId, validation.values.id)
      }
    }
  }, [pageView, allSurveyOptions, validation.values.surveyId])

  // Update survey URLs when lineOaId changes (to update cid param)
  useEffect(() => {
    if (validation.values.surveyId && validation.values.lineOaId) {
      const selectedSurvey = allSurveyOptions.find(s => String(s.id) === String(validation.values.surveyId))
      if (selectedSurvey && selectedSurvey.uuid_link) {
        setSurveyUrls(selectedSurvey.uuid_link, validation.values.lineOaId, validation.values.id)
      }
    }
  }, [validation.values.lineOaId, lineOaOptions])

  // Ensure create page URL includes lcid when next_id arrives after survey selection
  useEffect(() => {
    if (["create"].includes(pageView) && validation.values.id && validation.values.surveyId) {
      const selectedSurvey = allSurveyOptions.find(s => String(s.id) === String(validation.values.surveyId))
      if (selectedSurvey && selectedSurvey.uuid_link) {
        setSurveyUrls(selectedSurvey.uuid_link, validation.values.lineOaId, validation.values.id)
      }
    }
  }, [pageView, validation.values.id, validation.values.surveyId, validation.values.lineOaId, allSurveyOptions, lineOaOptions])

  const handleNextStep = () => {
    if (currentStep < 2) {
      // Validate Step 1 fields before proceeding
      if (currentStep === 1) {
        const step1Errors = []
        if (!validation.values.lineOaId || validation.errors.lineOaId) step1Errors.push("lineOaId")
        if (!validation.values.categoryId || validation.errors.categoryId) step1Errors.push("categoryId")
        if (!validation.values.surveyId || validation.errors.surveyId) step1Errors.push("surveyId")
        if (!validation.values.startDate || validation.errors.startDate) step1Errors.push("startDate")
        if (!validation.values.endDate || validation.errors.endDate) step1Errors.push("endDate")

        if (step1Errors.length > 0) {
          step1Errors.forEach(field => {
            validation.setFieldTouched(field, true)
          })
          return
        }
      }
      setCurrentStep(currentStep + 1)
    }
  }

  const handlePrevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1)
    }
  }

  const PageAction = pageView === "create" ? "Create" : "Edit"
  document.title = `Survey Line Campaign | ${PageAction} | ` + process.env.REACT_APP_CMS_TITLE

  if (isLoading) {
    return <Spinners setLoading={setIsLoading} />
  }

  return (
    <div className="page-content">
      <Container fluid={true}>
        {/* Breadcrumbs */}
        <Breadcrumbs
          title={"Survey Line Campaign"}
          breadcrumbItems={[
            { title: "Survey Line Campaign", link: "survey-line-campaign" },
            { title: `${PageAction} Campaign`, link: pageView },
          ]}
        />

        {/* Step Indicator */}
        {currentStep === 1 ? (
          <h4 className="mb-4" style={{ fontWeight: "600" }}>
            {PageAction} Campaign <span style={{ color: "#34c38f" }}>{currentStep}</span><span style={{ color: "#74788d" }}>/2</span>
          </h4>
        ) : (
          <h4 className="mb-4" style={{ fontWeight: "600" }}>
            Preview Campaign <span style={{ color: "#34c38f" }}>2</span><span style={{ color: "#74788d" }}>/2</span>
          </h4>
        )}

        {/* Step 1: All Campaign Details */}
        {currentStep === 1 && (
          <Step1
            validation={validation}
            pageView={pageView}
            categoryOptions={categoryOptions}
            surveyOptions={filteredSurveyOptions}
            lineOaOptions={lineOaOptions}
          />
        )}

        {/* Step 2: Preview Campaign */}
        {currentStep === 2 && (
          <Step2
            validation={validation}
            pageView={pageView}
            surveyName={selectedSurveyName}
            categoryOptions={categoryOptions}
            lineOaOptions={lineOaOptions}
          />
        )}

        {/* Navigation Buttons */}
        {currentStep === 1 ? (
          <Row className="mt-4">
            <Col style={{ display: "flex", justifyContent: "space-between" }}>
              <div className="d-flex gap-2">
                <Button
                  type="button"
                  outline
                  color="secondary"
                  style={{ minWidth: "120px", backgroundColor: "#fff", borderColor: "#ced4da", color: "#495057" }}
                  onClick={() => setShowCancelModal(true)}
                >
                  <i className="bx bx-x me-1"></i>
                  Cancel
                </Button>
              </div>
              <div>
                <Button
                  type="button"
                  color="success"
                  className="btn btn-success"
                  onClick={handleNextStep}
                >
                  Next
                </Button>
              </div>
            </Col>
          </Row>
        ) : (
          <Row className="mt-4">
            <Col style={{ display: "flex", justifyContent: "space-between" }}>
              {/* Left Side: Back, Cancel (สไตล์สีขาวเหมือน Order Campaign Step4) */}
              <div className="d-flex gap-2">
                <Button
                  type="button"
                  outline
                  color="secondary"
                  style={{ minWidth: "120px", backgroundColor: "#fff", borderColor: "#ced4da", color: "#495057" }}
                  onClick={handlePrevStep}
                >
                  <i className="bx bx-arrow-back me-1"></i>
                  Back
                </Button>
                <Button
                  type="button"
                  outline
                  color="secondary"
                  style={{ minWidth: "120px", backgroundColor: "#fff", borderColor: "#ced4da", color: "#495057" }}
                  onClick={() => setShowCancelModal(true)}
                >
                  <i className="bx bx-x me-1"></i>
                  Cancel
                </Button>
              </div>

              {/* Right Side: Save Draft, Confirm */}
              <div className="d-flex gap-2">
                <Button
                  type="button"
                  style={{
                    backgroundColor: "#fff",
                    border: "1px solid #34c38f",
                    color: "#34c38f",
                    minWidth: "120px",
                  }}
                  onClick={() => {
                    validation.setFieldValue("status", "draft")
                    setTimeout(() => validation.handleSubmit(), 0)
                  }}
                >
                  <i className="bx bx-save me-1"></i>
                  Save Draft
                </Button>
                <Button
                  type="button"
                  color="success"
                  style={{ minWidth: "120px" }}
                  onClick={() => {
                    validation.setFieldValue("status", "schedule")
                    setTimeout(() => validation.handleSubmit(), 0)
                  }}
                >
                  <i className="bx bx-check me-1"></i>
                  Confirm
                </Button>
              </div>
            </Col>
          </Row>
        )}

      </Container>

      {/* Cancel Confirmation Modal */}
      <CancelModal
        show={showCancelModal}
        onConfirm={() => {
          setShowCancelModal(false)
          navigate("/survey-line-campaign")
        }}
        onCancel={() => setShowCancelModal(false)}
      />
    </div>
  )
}

SurveyLineCampaignTemplate.propTypes = {
  t: PropTypes.any,
  pageView: PropTypes.string,
}

export default withTranslation()(SurveyLineCampaignTemplate)
